import type { Customer } from "./types";

export type CustomerStatus = "paid" | "trial" | "prospect";

/** 付费：成交价 > 0；试用：有 license 但未成交；其余为待跟进。 */
export function customerStatus(c: { price_cny?: number | null; license_count?: number }): CustomerStatus {
  const price = c.price_cny;
  if (typeof price === "number" && price > 0) return "paid";
  if ((c.license_count ?? 0) > 0) return "trial";
  return "prospect";
}

/** 签发 license 弹窗的默认「频道数 / 天数」，按是否已付费区分。 */
export function signDialogDefaults(customer: Customer): { maxTemplates: number; days: number } {
  const isPaid = typeof customer.price_cny === "number" && customer.price_cny > 0;
  if (!isPaid) {
    return { maxTemplates: 0, days: 3 };
  }
  const licenses = customer.licenses ?? [];
  const prev = licenses.length > 0 ? licenses[licenses.length - 1].max_templates : 0;
  return { maxTemplates: prev === 0 ? 1 : prev, days: 31 };
}

export function fmtTsShort(ts?: number | null): string {
  if (!ts) return "-";
  const d = new Date(ts * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function fmtTs(ts?: number | null): string {
  if (!ts) return "-";
  const d = new Date(ts * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

export function fmtDate(ts?: number | null): string {
  if (!ts) return "-";
  const d = new Date(ts * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** 返回当前本地时间的 `YYYY-MM-DDTHH:mm`，可直接喂给 <input type="datetime-local">。 */
export function nowLocalDatetime(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/** 返回 `今 + days 天` 的本地时间字符串，用于 datetime-local 默认值。 */
export function plusDaysLocalDatetime(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/**
 * 把 <input type="datetime-local"> 拿到的本地时间字符串（`YYYY-MM-DDTHH:mm`，无时区信息）
 * 转成带本地时区偏移的 ISO 8601 字符串，例如 `2026-12-31T23:59:00+08:00`。
 *
 * 这样后端的 `parse_iso` 会把它理解成用户实际选的那一刻（本地时间），而不是 UTC。
 */
export function localDatetimeToIso(local: string): string {
  if (!local) return "";
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return local;
  const pad = (n: number) => String(n).padStart(2, "0");
  const offsetMin = -d.getTimezoneOffset();
  const sign = offsetMin >= 0 ? "+" : "-";
  const absMin = Math.abs(offsetMin);
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}` +
    `${sign}${pad(Math.floor(absMin / 60))}:${pad(absMin % 60)}`
  );
}

export function isExpired(notAfter?: number): boolean {
  if (!notAfter) return false;
  return notAfter * 1000 < Date.now();
}

export function daysLeft(notAfter?: number): number | null {
  if (!notAfter) return null;
  const ms = notAfter * 1000 - Date.now();
  return Math.floor(ms / 86400000);
}

export interface RemainingParts {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalSec: number;
}

/** 把「剩余秒数」拆成「几天 几小时 几分 几秒」。负数全部归零。 */
export function splitRemaining(totalSec: number): RemainingParts {
  if (totalSec <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, totalSec: 0 };
  let s = Math.floor(totalSec);
  const days = Math.floor(s / 86400);
  s -= days * 86400;
  const hours = Math.floor(s / 3600);
  s -= hours * 3600;
  const minutes = Math.floor(s / 60);
  s -= minutes * 60;
  return { days, hours, minutes, seconds: s, totalSec: Math.floor(totalSec) };
}

/** 格式化「几天几小时几分几秒」。即使天数为 0 也会带上「0天」让语义更明确。 */
export function formatRemaining(totalSec: number): string {
  const { days, hours, minutes, seconds } = splitRemaining(totalSec);
  return `${days}天 ${hours}小时 ${minutes}分 ${seconds}秒`;
}

export function shortFingerprint(fp?: string, len = 13): string {
  if (!fp) return "-";
  if (fp.length <= len) return fp;
  return fp.slice(0, len) + "…";
}

export function classNames(...names: (string | false | null | undefined)[]): string {
  return names.filter(Boolean).join(" ");
}
